import type { IntlShape } from 'react-intl';

import { Targets } from '@atlassiansox/cross-flow-support';

type JiraCustomerServiceTarget = 'jira-customer-service';
type TargetProduct = (typeof Targets)[keyof typeof Targets] | JiraCustomerServiceTarget;

type ProjectNameMessageDescriptor = {
	defaultMessage: string;
	description: string;
	id: string;
};

const JSM_GENERAL_SERVICE_MANAGEMENT_KEY = 'jsm.general-service-management';
const JSM_GENERAL_SERVICE_MANAGEMENT_CMP_KEY =
	'com.atlassian.servicedesk:company-managed-general-service-project';
const JSM_IT_SERVICE_MANAGEMENT_KEY = 'jsm.it-service-management';
const JSM_IT_SERVICE_MANAGEMENT_BASIC_KEY = 'jsm.it-service-management-basic';
const JSM_IT_SERVICE_MANAGEMENT_ADVANCED_KEY = 'jsm.it-service-management-advanced';
const JSM_BLANK_SERVICE_MANAGEMENT_KEY = 'jsm.blank-service-management';
const JSM_IT_SERVICE_MANAGEMENT_BASIC_CMP_KEY =
	'com.atlassian.servicedesk:simplified-it-service-management-basic';
const JSM_IT_SERVICE_MANAGEMENT_ADVANCED_CMP_KEY =
	'com.atlassian.servicedesk:simplified-it-service-management';
const JSM_CUSTOMER_SERVICE_MANAGEMENT_KEY = 'jsm.customer-service-management';
const JSM_CUSTOMER_SERVICE_MANAGEMENT_CMP_KEY = 'com.atlassian.jcs:customer-service-management';

const JIRA_CUSTOMER_SERVICE_TARGET: JiraCustomerServiceTarget = 'jira-customer-service';

const JPD_PRODUCT_DISCOVERY_KEY = 'jpd.product-discovery';
const JPD_PRODUCT_DISCOVERY_TMP_KEY = 'jira.polaris:discovery';
const JPD_PRODUCT_ROADMAP_KEY = 'jpd.product-roadmap';
const JPD_PRODUCT_ROADMAP_TMP_KEY = 'jira.polaris:roadmap';
const JPD_PRIORITIZATION_KEY = 'jpd.prioritization';
const JPD_PRIORITIZATION_TMP_KEY = 'jira.polaris:prioritization';

const projectNameMessages = {
	support: {
		id: 'jsm-auto-create-project-name.support',
		description:
			'This is the name for a project that is automatically created when cross-flowing to JSM. This will be the name used for the "General service management for IT teams" template',
		defaultMessage: 'Support',
	},
	requests: {
		id: 'jsm-auto-create-project-name.requests',
		description:
			'This is the name for a project that is automatically created when cross-flowing to JSM. This will be the name used for the "General service management for business teams" template',
		defaultMessage: 'Requests',
	},
	customerService: {
		id: 'jsm-auto-create-project-name.customer-service',
		description:
			'This is the name for a project that is automatically created when cross-flowing to JSM. This will be the name used for the "Customer service management" template',
		defaultMessage: 'Customer service',
	},
	ideas: {
		id: 'jpd-auto-create-project-name.ideas',
		description:
			'This is the name for a project that is automatically created when cross-flowing to JPD. This will be the name used for the "Product discovery" template',
		defaultMessage: 'My discovery project',
	},
	prioritization: {
		id: 'jpd-auto-create-project-name.prioritization',
		description:
			'This is the name for a project that is automatically created when cross-flowing to JPD. This will be the name used for the "Prioritization" template',
		defaultMessage: 'Prioritization',
	},
} satisfies Record<string, ProjectNameMessageDescriptor>;

const jsmSupportTemplateKeys = new Set([
	JSM_GENERAL_SERVICE_MANAGEMENT_KEY,
	JSM_GENERAL_SERVICE_MANAGEMENT_CMP_KEY,
	JSM_IT_SERVICE_MANAGEMENT_KEY,
	JSM_IT_SERVICE_MANAGEMENT_BASIC_KEY,
	JSM_IT_SERVICE_MANAGEMENT_ADVANCED_KEY,
	JSM_BLANK_SERVICE_MANAGEMENT_KEY,
	JSM_IT_SERVICE_MANAGEMENT_BASIC_CMP_KEY,
	JSM_IT_SERVICE_MANAGEMENT_ADVANCED_CMP_KEY,
]);

const csmTemplateKeys = new Set([
	JSM_CUSTOMER_SERVICE_MANAGEMENT_KEY,
	JSM_CUSTOMER_SERVICE_MANAGEMENT_CMP_KEY,
]);

const jpdProjectNameMessageByTemplateKey = new Map<string, ProjectNameMessageDescriptor>([
	[JPD_PRODUCT_DISCOVERY_KEY, projectNameMessages.ideas],
	[JPD_PRODUCT_DISCOVERY_TMP_KEY, projectNameMessages.ideas],
	// Keep roadmap aligned with growth-kit control:
	// cross-flow-frontend/src/common/services/create-project/constants.ts maps
	// PRODUCT_ROADMAP_KEY and PRODUCT_ROADMAP_TMP_KEY to messages.ideas.
	[JPD_PRODUCT_ROADMAP_KEY, projectNameMessages.ideas],
	[JPD_PRODUCT_ROADMAP_TMP_KEY, projectNameMessages.ideas],
	[JPD_PRIORITIZATION_KEY, projectNameMessages.prioritization],
	[JPD_PRIORITIZATION_TMP_KEY, projectNameMessages.prioritization],
]);

const defaultProjectNameMessageByTargetProduct = new Map<
	TargetProduct,
	ProjectNameMessageDescriptor
>([
	[Targets.JIRA_SERVICE_DESK, projectNameMessages.requests],
	[Targets.JIRA_PRODUCT_DISCOVERY, projectNameMessages.ideas],
	[JIRA_CUSTOMER_SERVICE_TARGET, projectNameMessages.customerService],
]);

const getProjectNameMessage = (
	targetProduct: TargetProduct,
	templateKey: string,
): ProjectNameMessageDescriptor | undefined => {
	if (targetProduct === Targets.JIRA_SERVICE_DESK && jsmSupportTemplateKeys.has(templateKey)) {
		return projectNameMessages.support;
	}

	if (targetProduct === JIRA_CUSTOMER_SERVICE_TARGET && csmTemplateKeys.has(templateKey)) {
		return projectNameMessages.customerService;
	}

	if (targetProduct === Targets.JIRA_PRODUCT_DISCOVERY) {
		return (
			jpdProjectNameMessageByTemplateKey.get(templateKey) ??
			defaultProjectNameMessageByTargetProduct.get(targetProduct)
		);
	}

	return defaultProjectNameMessageByTargetProduct.get(targetProduct);
};

export const getProjectCreationContextParams = ({
	targetProduct,
	templateKey,
	formatMessage,
}: {
	formatMessage: IntlShape['formatMessage'];
	targetProduct: TargetProduct;
	templateKey?: string;
}): Record<string, unknown> | undefined => {
	if (!templateKey) {
		return undefined;
	}

	const projectNameMessage = getProjectNameMessage(targetProduct, templateKey);
	const projectName = projectNameMessage ? formatMessage(projectNameMessage) : undefined;

	return {
		templateKey,
		...(projectName ? { projectName } : {}),
	};
};
